import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, useInView } from 'framer-motion'
import { PenTool, Wrench, CheckCircle } from 'lucide-react'

interface ApproachStep {
  key: string
  number: string
  title: string
  description: string
  icon: React.ComponentType<{ className?: string }>
}

export function IntegratedApproachSection() {
  const { t } = useTranslation('pages')
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const steps: ApproachStep[] = [
    {
      key: 'design',
      number: '01',
      title: t('design.title'),
      description: t('design.subtitle'),
      icon: PenTool,
    },
    {
      key: 'manufacturing',
      number: '02',
      title: t('manufacturing.title'),
      description: t('manufacturing.subtitle'),
      icon: Wrench,
    },
    {
      key: 'finalStage',
      number: '03',
      title: t('finalStage.title'),
      description: t('finalStage.subtitle'),
      icon: CheckCircle,
    },
  ]

  return (
    <section ref={ref} className="relative overflow-hidden bg-slate-50 py-16 md:py-24">
      {/* Decorative background blobs */}
      <div className="pointer-events-none absolute -right-20 -top-20 h-[500px] w-[500px] rounded-full bg-[#f75e47]/10 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-20 -left-20 h-[500px] w-[500px] rounded-full bg-slate-900/5 blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-4 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          className="mx-auto max-w-3xl text-center"
        >
          <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-[#f75e47]">
            {t('processes.approachLabel')}
          </span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900 md:text-4xl">
            {t('processes.approachTitle')}
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">
            {t('processes.approachDesc')}
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative mt-14 md:mt-20">
          {/* Desktop connecting line background */}
          <div className="absolute left-0 right-0 top-10 hidden h-0.5 bg-slate-200 md:block" />
          {/* Desktop animated foreground line */}
          <motion.div
            className="absolute left-0 top-10 hidden h-0.5 bg-[#f75e47] md:block"
            initial={{ width: 0 }}
            animate={isInView ? { width: '100%' } : { width: 0 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          />

          {/* Mobile connecting line background */}
          <div className="absolute bottom-0 left-[27px] top-0 w-0.5 bg-slate-200 md:hidden" />
          {/* Mobile animated foreground line */}
          <motion.div
            className="absolute left-[27px] top-0 w-0.5 bg-[#f75e47] md:hidden"
            initial={{ height: 0 }}
            animate={isInView ? { height: '100%' } : { height: 0 }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          />

          <div className="relative grid gap-10 md:grid-cols-3 md:gap-8">
            {steps.map((step, idx) => {
              const Icon = step.icon
              return (
                <motion.div
                  key={step.key}
                  initial={{ opacity: 0, y: 24 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{
                    duration: 0.5,
                    delay: 0.6 + idx * 0.15,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                  className="relative flex items-start gap-5 md:flex-col md:items-center md:text-center"
                >
                  {/* Icon / Number circle */}
                  <div className="relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-2 border-slate-100 bg-white shadow-[0_4px_6px_-1px_rgba(0,0,0,0.08),0_10px_30px_-5px_rgba(0,0,0,0.05)] md:h-20 md:w-20">
                    <Icon className="h-6 w-6 text-slate-900 md:h-8 md:w-8" />
                    <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#f75e47] text-[10px] font-bold text-white md:-right-2 md:-top-2 md:h-6 md:w-6 md:text-xs">
                      {step.number}
                    </span>
                  </div>

                  {/* Text content */}
                  <div className="pt-0.5 md:pt-2">
                    <h3 className="text-lg font-semibold text-slate-900 md:text-xl">
                      {step.title}
                    </h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600 md:mt-2 md:text-base">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
